import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MessageConfig } from "@/components/generator/MessageConfig";
import { ChatGenerator, type ChatState, type RoleKey } from "@/components/generator/ChatGenerator";
import { GeneratedMessage } from "@/components/generator/GeneratedMessage";
import { MessageHistoryModal } from "@/components/generator/MessageHistoryModal";

import { DevModePanel, DevModeData } from "@/components/generator/DevModePanel";
import { Button } from "@/components/ui/button";
import { Loader2, User, AlertCircle, ExternalLink, History } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import { getErrorMessage, isNetworkError, getNetworkErrorMessage } from "@/lib/error-handler";
import { messageApi, ttsApi, ttsCleanupApi } from "@/lib/api";

import { prepareTTSText } from "@/lib/tts-utils";
import { useProfileTabs } from "@/contexts/ProfileTabsContext";
import type { MessageRequest, ScrapedData, GenerationState, Sender } from "@/types/api";

interface ProjectData {
  id: number;
  name: string;
  prompt_id: string | null;
}

export type ProfileTabStatus = 'scraping' | 'ready' | 'error';

const defaultConfig: Partial<MessageRequest> = {
  project_id: 0,
  project_name: "",
  scenario: "sales-classic",
  message_type: "invitation",
  message_length: "medium",
  formality_level: "semiformal",
  language: "pl",
  writing_style: "service-listing",
  sex: "male",
  cta_option: "none",
  prompt_id: "",
  activity_reference: false,
};

export interface ProfileTabData {
  id: string;
  profileUrl: string;
  profileName: string;
  status: ProfileTabStatus;
  scrapedData?: ScrapedData;
  error?: string;
  activityReference: boolean;
  initialConfig: Partial<MessageRequest>;
  // Persisted state
  config: Partial<MessageRequest>;
  generationState: GenerationState;
  // Persisted per-role chat state (survives tab switches)
  chatState?: Record<RoleKey, ChatState>;
  // Dev mode data
  devModeData?: DevModeData;
}

interface ProfileTabProps {
  tabData: ProfileTabData;
  projects: ProjectData[];
  devModeEnabled: boolean;
  onConfigChange: (
    id: string,
    config: Partial<MessageRequest> | ((prev: Partial<MessageRequest>) => Partial<MessageRequest>)
  ) => void;
  onGenerationStateChange: (id: string, state: GenerationState) => void;
  onDevModeDataChange: (id: string, data: DevModeData) => void;
  onChatStateChange?: (id: string, state: Record<RoleKey, ChatState>) => void;
}


export function ProfileTab({ 
  tabData, 
  projects,
  devModeEnabled,
  onConfigChange,
  onGenerationStateChange,
  onDevModeDataChange,
  onChatStateChange,
}: ProfileTabProps) {

  const { t } = useTranslation('generator');
  const { t: tc } = useTranslation('common');
  const { fetchOrGetCachedProject } = useProfileTabs();
  const config = tabData.config;
  const generationState = tabData.generationState;
  const generationStateRef = useRef(generationState);

  useEffect(() => {
    generationStateRef.current = generationState;
  }, [generationState]);
  
  
  
  const setConfig = (newConfig: Partial<MessageRequest> | ((prev: Partial<MessageRequest>) => Partial<MessageRequest>)) => {
    // Forward functional updates so the context resolves them against the LATEST tab state.
    // (Resolving against `config` from this render closure caused sequential setConfig calls
    // in the same tick to clobber each other — e.g. project_id getting reset to 0.)
    onConfigChange(tabData.id, newConfig);
  };
  
  const setGenerationState = (newState: GenerationState | ((prev: GenerationState) => GenerationState)) => {
    const nextState = typeof newState === 'function'
      ? newState(generationStateRef.current)
      : newState;

    generationStateRef.current = nextState;
    onGenerationStateChange(tabData.id, nextState);
  };

  // Senders from cache (project-specific)
  const [senders, setSenders] = useState<Sender[]>([]);
  const [isLoadingSenders, setIsLoadingSenders] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // Audio version state (kept on generationState for tab persistence)

  // Update config when scraping completes

  // Set first project if none selected
  useEffect(() => {
    if (projects.length > 0 && !config.project_name) {
      const firstProject = projects[0];
      setConfig(prev => ({ ...prev, project_name: firstProject.name }));
    }
  }, [projects]);

  // Fetch project details when project changes (uses cache)
  const fetchProjectDetails = useCallback(async (projectName: string) => {
    if (!projectName) return;

    setIsLoadingSenders(true);
    try {
      const cached = await fetchOrGetCachedProject(projectName);
      
      if (cached) {
        setConfig(prev => ({ 
          ...prev, 
          project_id: cached.projectId,
          prompt_id: cached.promptId || prev.prompt_id 
        }));
        setSenders(cached.senders);

        if (cached.senders.length === 1) {
          const singleSender = cached.senders[0];
          setConfig(prev => ({ ...prev, sender: `${singleSender.name} ${singleSender.surname}` }));
        } else {
          setConfig(prev => {
            const currentSenderExists = cached.senders.some(
              s => `${s.name} ${s.surname}` === prev.sender
            );
            if (currentSenderExists) return prev;
            // Try to preserve sender from initial config (carried over from main panel)
            const initialSender = tabData.initialConfig?.sender;
            const initialSenderExists = initialSender && cached.senders.some(
              s => `${s.name} ${s.surname}` === initialSender
            );
            if (initialSenderExists) {
              return { ...prev, sender: initialSender };
            }
            return { ...prev, sender: undefined };
          });
        }
      } else {
        setSenders([]);
      }
    } finally {
      setIsLoadingSenders(false);
    }
  }, [fetchOrGetCachedProject]);

  useEffect(() => {
    if (config.project_name) {
      fetchProjectDetails(config.project_name);
    }
  }, [config.project_name, fetchProjectDetails]);

  const handleGenerate = async () => {
    // Validate project_id (number field)
    if (!config.project_id) {
      toast({ title: tc('error'), description: t('profileTab.selectProjectFirst'), variant: "destructive" });
      return;
    }

    if (!config.sender) {
      toast({ title: tc('error'), description: t('profileTab.selectSenderFirst'), variant: "destructive" });
      return;
    }

    const requiredFields = ['scenario', 'writing_style', 'language', 'prompt_id', 'message_type', 'sex'] as const;
    for (const field of requiredFields) {
      if (!config[field]) {
        toast({ title: tc('error'), description: t('profileTab.fillRequired', { field }), variant: "destructive" });
        return;
      }
    }

    if (config.scenario === 'sales-common-contacts-quantity' && !config.common_contacts_count) {
      toast({ title: tc('error'), description: t('profileTab.fillCommonContactsCount'), variant: "destructive" });
      return;
    }

    if (config.scenario === 'sales-common-contacts-people' && (!config.common_contact_people || config.common_contact_people.length === 0)) {
      toast({ title: tc('error'), description: t('profileTab.fillCommonContactsPeople'), variant: "destructive" });
      return;
    }

    if (!config.profile_data && !tabData.scrapedData) {
      toast({ title: tc('error'), description: t('profileTab.noProfileData'), variant: "destructive" });
      return;
    }

    if (config.message_type === 'appointment-request-calendly') {
      const selectedSender = senders.find(s => `${s.name} ${s.surname}` === config.sender);
      if (!selectedSender?.calendly_link) {
        toast({ title: tc('error'), description: t('profileTab.noCalendlyLink'), variant: "destructive" });
        return;
      }
    }

    setGenerationState({ status: "pending" });
    const startTime = Date.now();

    try {
      // Find selected sender for calendly_link
      const selectedSender = senders.find(
        s => `${s.name} ${s.surname}` === config.sender
      );

      // Ensure profile_data has Firma field (required by backend API)
      let profileData = config.profile_data || tabData.scrapedData?.profileDataText || "";
      if (profileData && !profileData.includes("Firma:")) {
        // Extract company from first experience entry if available
        const expMatch = profileData.match(/Doświadczenie:\s*([^;]+)/);
        if (expMatch) {
          const firstExp = expMatch[1];
          const companyMatch = firstExp.match(/\bat\s+(.+?)(?:;|$)/i);
          if (companyMatch && companyMatch[1]) {
            const company = companyMatch[1].trim();
            // Insert Firma after Nagłówek line
            profileData = profileData.replace(
              /(Nagłówek:[^\n]*\n)/,
              `$1Firma: ${company}\n`
            );
          }
        }
      }

      const payload: Record<string, unknown> = {
        llm_provider: config.llm_provider,
        project_id: config.project_id,
        project_name: config.project_name,
        scenario: config.scenario,
        writing_style: config.writing_style,
        language: config.language,
        profile_data: profileData,
        prompt_id: config.prompt_id,
        message_type: config.message_type,
        sex: config.sex,
        sender: config.sender,
        message_length: config.message_length,
        formality_level: config.formality_level,
        cta_option: config.cta_option,
        cta_link: config.cta_link,
        activity_reference: config.activity_reference,
        profile_url: tabData.profileUrl,
        profile_name: tabData.scrapedData?.profile?.name,
      };

      // Add calendly_link when message type is appointment-request-calendly
      if (config.message_type === 'appointment-request-calendly' && selectedSender?.calendly_link) {
        payload.calendly_link = selectedSender.calendly_link;
      }

      if (config.scenario === 'sales-common-contacts-quantity' && config.common_contacts_count) {
        payload.common_contacts_count = config.common_contacts_count;
      }
      if (config.scenario === 'sales-common-contacts-people' && config.common_contact_people) {
        payload.common_contact_people = config.common_contact_people;
      }

      // Previous messages — only for followup / end-conversation
      if (
        (config.message_type === 'followup' || config.message_type === 'end-conversation') &&
        Array.isArray(config.previous_messages) &&
        config.previous_messages.length > 0
      ) {
        payload.previous_messages = config.previous_messages;
      }



      console.log("Sending payload to message-generator:", payload);

      // Save request data for dev mode
      const requestTimestamp = new Date().toISOString();
      onDevModeDataChange(tabData.id, {
        requestPayload: payload,
        requestTimestamp,
      });

      const response = await authFetch(
        messageApi(),
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      const responseTimestamp = new Date().toISOString();

      if (!response.ok) {
        console.error("Edge function error:", data);
        onDevModeDataChange(tabData.id, {
          requestPayload: payload,
          requestTimestamp,
          responseData: data,
          responseTimestamp,
          error: data.error || `HTTP ${response.status}`,
        });
        
        const errMsg = getErrorMessage({
          status: response.status,
          errorCode: data.error_code,
          context: 'generator',
          field: data.field,
          env: data.env,
        });
        
        throw { title: errMsg.title, description: errMsg.description || data.details || '' };
      }

      // Save success response for dev mode
      onDevModeDataChange(tabData.id, {
        requestPayload: payload,
        requestTimestamp,
        responseData: data,
        responseTimestamp,
      });

      const generatedMessage = data.generated_message || data.message || data.content || JSON.stringify(data);

      setGenerationState({
        status: "success",
        result: {
          generated_message: generatedMessage,
          context_used: data.context_used || [],
          project_id: config.project_name || "",
        },
        generationTime: Date.now() - startTime,
        audioText: "",
        audioTextLoading: true,
        audioTextError: null,
      });

      toast({ title: tc('success'), description: t('profileTab.messageGenerated') });

      // Auto-fetch audio version (preview text only, no TTS)
      void fetchAudioVersion(generatedMessage);

    } catch (error) {
      console.error("Generation error:", error);
      
      if (isNetworkError(error)) {
        const netErr = getNetworkErrorMessage();
        setGenerationState({ status: "error", error: netErr.title });
        toast({ title: netErr.title, description: netErr.description, variant: "destructive" });
        return;
      }
      
      // Structured error from our throw above
      if (error && typeof error === 'object' && 'title' in error) {
        const e = error as { title: string; description: string };
        setGenerationState({ status: "error", error: e.title });
        toast({ title: e.title, description: e.description, variant: "destructive" });
        return;
      }

      const fallback = error instanceof Error ? error.message : t('profileTab.unknownError');
      setGenerationState({ status: "error", error: fallback });
      toast({ title: tc('error'), description: fallback, variant: "destructive" });
    }
  };

  const runTTS = async (textToSend: string) => {
    if (!generationState.result?.generated_message) {
      toast({ title: tc('error'), description: t('profileTab.generateTextFirst'), variant: "destructive" });
      return;
    }
    const selectedSender = senders.find(s => `${s.name} ${s.surname}` === config.sender);
    if (!selectedSender?.voice_id) {
      toast({ title: tc('error'), description: t('profileTab.selectSenderWithVoice'), variant: "destructive" });
      return;
    }

    setIsGeneratingAudio(true);
    toast({ title: t('toast.generatingAudio'), description: t('profileTab.generatingAudioToast') });

    const originalText = generationState.result.generated_message;
    const normalizedText = textToSend;

    try {
      const response = await authFetch(ttsApi(), {
        method: 'POST',
        body: JSON.stringify({ text: normalizedText, voice_id: selectedSender.voice_id, ffmpeg_speed: 1.0 }),
      });

      const ttsConfig = {
        seed: response.headers.get('X-TTS-Seed') === 'none' ? 'none' as const : parseInt(response.headers.get('X-TTS-Seed') || '0'),
        attempt: parseInt(response.headers.get('X-TTS-Attempt') || '1'),
        language_code: 'pl',
        apply_text_normalization: 'on',
        voice_settings: {
          stability: parseFloat(response.headers.get('X-TTS-Stability') || '0.75'),
          similarity_boost: parseFloat(response.headers.get('X-TTS-Similarity') || '0.65'),
          style: parseFloat(response.headers.get('X-TTS-Style') || '0.1'),
          speed: parseFloat(response.headers.get('X-TTS-Speed') || '0.95'),
          use_speaker_boost: true,
        },
      };

      onDevModeDataChange(tabData.id, {
        ...tabData.devModeData,
        ttsRequest: {
          originalText,
          normalizedText,
          voiceId: selectedSender.voice_id,
          timestamp: new Date().toISOString(),
          config: ttsConfig,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errMsg = getErrorMessage({
          status: response.status,
          errorCode: errorData.error_code,
          context: 'tts',
        });
        throw new Error(errMsg.title);
      }

      const audioBuffer = await response.arrayBuffer();
      const audioBlob = new Blob([audioBuffer], { type: 'video/mp4' });
      const audioUrl = URL.createObjectURL(audioBlob);

      setGenerationState(prev => ({
        ...prev,
        result: prev.result ? {
          ...prev.result,
          audioUrl,
          rawAudioBlob: audioBlob,
        } : undefined,
      }));

      toast({ title: tc('success'), description: t('profileTab.audioGenerated') });
    } catch (error) {
      console.error("Audio generation error:", error);
      if (isNetworkError(error)) {
        const netErr = getNetworkErrorMessage();
        toast({ title: netErr.title, description: netErr.description, variant: "destructive" });
      } else {
        toast({ title: tc('error'), description: error instanceof Error ? error.message : t('profileTab.audioError'), variant: "destructive" });
      }
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const fetchAudioVersion = async (originalTextOverride?: string) => {
    const originalText = originalTextOverride ?? generationState.result?.generated_message;
    if (!originalText) {
      toast({ title: tc('error'), description: t('profileTab.generateTextFirst'), variant: "destructive" });
      return;
    }

    setGenerationState(prev => ({
      ...prev,
      audioText: prev.audioText ?? "",
      audioTextLoading: true,
      audioTextError: null,
    }));

    try {
      const language = config.language || generationState.lastRequest?.language || 'pl';
      const response = await authFetch(ttsCleanupApi(), {
        method: 'POST',
        body: JSON.stringify({ original_message: originalText, language }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || t('audioVersion.errorDefault'));
      }
      const data = await response.json();
      const cleaned = data.cleaned_message || prepareTTSText(originalText);
      setGenerationState(prev => ({
        ...prev,
        audioText: cleaned,
        audioTextLoading: false,
        audioTextError: null,
      }));
    } catch (err) {
      console.error('TTS cleanup error:', err);
      setGenerationState(prev => ({
        ...prev,
        audioTextLoading: false,
        audioTextError: err instanceof Error ? err.message : t('audioVersion.errorDefault'),
      }));
    }
  };

  const handleAudioTextChange = (text: string) => {
    setGenerationState(prev => ({ ...prev, audioText: text }));
  };

  const handleUseLocalAudio = () => {
    const original = generationState.result?.generated_message || "";
    setGenerationState(prev => ({
      ...prev,
      audioText: prepareTTSText(original),
      audioTextError: null,
    }));
  };

  const handleGenerateAudio = (textOverride?: string) => {
    const text = textOverride
      ?? generationState.audioText
      ?? prepareTTSText(generationState.result?.generated_message || "");
    if (!text.trim()) {
      toast({ title: tc('error'), description: t('profileTab.generateTextFirst'), variant: "destructive" });
      return;
    }
    void runTTS(text);
  };

  const handleReset = () => {
    setConfig({
      ...tabData.initialConfig,
      profile_data: tabData.scrapedData?.profileDataText,
      activity_reference: tabData.activityReference,
    });
    setGenerationState({ status: "idle" });
    toast({
      title: "Reset",
      description: "Konfiguracja została zresetowana",
    });
  };

  const handleMessageEdit = (message: string) => {
    if (generationState.result) {
      setGenerationState({
        ...generationState,
        result: {
          ...generationState.result,
          generated_message: message,
        },
      });
    }
  };

  const handleDownloadRawAudio = () => {
    if (generationState.result?.rawAudioBlob) {
      const url = URL.createObjectURL(generationState.result.rawAudioBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `diagnostic-audio-${Date.now()}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('=== RAW AUDIO DOWNLOAD ===');
      console.log('Downloaded blob size:', generationState.result.rawAudioBlob.size, 'bytes');
      console.log('Downloaded blob type:', generationState.result.rawAudioBlob.type);
      console.log('==========================');
      
      toast({
        title: "Pobrano",
        description: `Surowy plik audio (${(generationState.result.rawAudioBlob.size / 1024).toFixed(1)} KB) - sprawdź w playerze offline`,
      });
    }
  };

  const handleDownloadAudioAtSpeed = async (speed: number) => {
    const text = generationState.audioText
      ?? prepareTTSText(generationState.result?.generated_message || "");
    if (!text.trim()) {
      toast({ title: tc('error'), description: t('profileTab.generateTextFirst'), variant: "destructive" });
      throw new Error('No text');
    }
    const selectedSender = senders.find(s => `${s.name} ${s.surname}` === config.sender);
    if (!selectedSender?.voice_id) {
      toast({ title: tc('error'), description: t('profileTab.selectSenderWithVoice'), variant: "destructive" });
      throw new Error('No voice');
    }

    const response = await authFetch(ttsApi(), {
      method: 'POST',
      body: JSON.stringify({
        text,
        voice_id: selectedSender.voice_id,
        ffmpeg_speed: speed,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errMsg = getErrorMessage({
        status: response.status,
        errorCode: errorData.error_code,
        context: 'tts',
      });
      throw new Error(errMsg.title);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const speedLabel = speed.toString().replace('.', '_');
    link.download = `wiadomosc-audio-${speedLabel}x-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Build params for the chat (beta) endpoint. Returns null + toasts if config invalid.
  const buildChatParams = (messageType: MessageRequest["message_type"], previousMessages?: string[]) => {
    if (!config.project_id) {
      toast({ title: tc('error'), description: t('profileTab.selectProjectFirst'), variant: 'destructive' });
      return null;
    }
    if (!config.sender) {
      toast({ title: tc('error'), description: t('profileTab.selectSenderFirst'), variant: 'destructive' });
      return null;
    }
    let profileData = config.profile_data || tabData.scrapedData?.profileDataText || "";
    if (profileData && !profileData.includes("Firma:")) {
      const expMatch = profileData.match(/Doświadczenie:\s*([^;]+)/);
      if (expMatch) {
        const companyMatch = expMatch[1].match(/\bat\s+(.+?)(?:;|$)/i);
        if (companyMatch?.[1]) {
          profileData = profileData.replace(/(Nagłówek:[^\n]*\n)/, `$1Firma: ${companyMatch[1].trim()}\n`);
        }
      }
    }

    // If scrape-time research is available, fold its context into profile_data
    // and disable backend research to avoid a redundant call.
    const scrapedResearch = tabData.scrapedData?.research;
    const hasScrapedResearch = Boolean(scrapedResearch?.context);
    if (hasScrapedResearch && scrapedResearch?.context && !profileData.includes('Research firmy')) {
      profileData = `${profileData}\n\n${scrapedResearch.context}`;
    }

    const params: Record<string, unknown> = {
      llm_provider: config.llm_provider || 'gemini',
      project_id: config.project_id,
      project_name: config.project_name,
      scenario: config.scenario || 'sales-classic',
      writing_style: config.writing_style || 'service-listing',
      language: config.language || 'pl',
      profile_data: profileData,
      prompt_id: config.prompt_id,
      message_type: messageType,
      sex: config.sex || 'male',
      sender: config.sender,
      message_length: config.message_length || 'medium',
      formality_level: config.formality_level || 'semiformal',
      // v4 API rejects cta_option="none" — omit it entirely in that case.
      ...(config.cta_option && config.cta_option !== 'none'
        ? { cta_option: config.cta_option, cta_link: config.cta_link }
        : {}),
      activity_reference: config.activity_reference,
      research_mode: hasScrapedResearch ? false : ((config as any).research_mode !== false),
      research_country: scrapedResearch?.country || (config as any).research_country || 'Poland',
    };
    if (previousMessages && previousMessages.length > 0) params.previous_messages = previousMessages;
    return params;
  };

  if (tabData.status === 'scraping') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p className="text-lg font-medium">{t('profileTab.scraping')}</p>
        <p className="text-sm">{tabData.profileName}</p>
      </div>
    );
  }

  // Error state
  if (tabData.status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-destructive">
        <AlertCircle className="h-8 w-8 mb-4" />
        <p className="text-lg font-medium">{t('profileTab.errorTitle')}</p>
        <p className="text-sm text-muted-foreground mt-2">{tabData.error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column - Profile Info & Chat */}
        <div className="space-y-6">
          {/* Profile Summary Card */}
          {tabData.scrapedData && (
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start gap-4">
                {tabData.scrapedData.profile.profilePicture ? (
                  <img
                    src={tabData.scrapedData.profile.profilePicture}
                    alt={tabData.scrapedData.profile.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-primary"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-xl">
                    {tabData.scrapedData.profile.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg truncate">{tabData.scrapedData.profile.name}</h3>
                    <a
                      href={tabData.profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                      title="Otwórz profil LinkedIn"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{tabData.scrapedData.profile.headline}</p>
                  {tabData.scrapedData.profile.location && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {typeof tabData.scrapedData.profile.location === 'object'
                        ? (tabData.scrapedData.profile.location as any).full || (tabData.scrapedData.profile.location as any).city
                        : tabData.scrapedData.profile.location}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryModalOpen(true)}
                  className="shrink-0"
                >
                  <History className="h-4 w-4 mr-2" />
                  Historyczne ustawienia
                </Button>
              </div>
            </div>
          )}

          {(
            <ChatGenerator
              tabData={tabData}
              buildParams={buildChatParams}
              initialChatState={tabData.chatState}
              onChatStateChange={(state) => onChatStateChange?.(tabData.id, state)}

              onTurnComplete={(turn) => {
                setGenerationState({
                  status: "success",
                  result: {
                    generated_message: turn.generated,
                    section1: turn.section1,
                    section2: turn.section2,
                    section3: turn.section3,
                    context_used: [],
                    project_id: config.project_name || "",
                  },
                  generationTime: 0,
                  audioText: "",
                  audioTextLoading: true,
                  audioTextError: null,
                });
                onDevModeDataChange(tabData.id, {
                  requestPayload: turn.requestPayload as Record<string, unknown>,
                  requestTimestamp: turn.requestTimestamp,
                  responseData: turn.responseData,
                  responseTimestamp: turn.responseTimestamp,
                });
                void fetchAudioVersion(turn.generated);
              }}
            />
          )}

          {generationState.status !== 'idle' && (
            <GeneratedMessage
              generationState={generationState}
              onMessageEdit={handleMessageEdit}
              onRegenerateAudio={(textOverride) => handleGenerateAudio(textOverride)}
              onDownloadRawAudio={handleDownloadRawAudio}
              onDownloadAudioAtSpeed={handleDownloadAudioAtSpeed}
              onPrepareAudioText={(text) => fetchAudioVersion(text)}
              onUseLocalAudioText={handleUseLocalAudio}
              onAudioTextChange={handleAudioTextChange}
              audioText={generationState.audioText}
              audioTextLoading={generationState.audioTextLoading}
              audioTextError={generationState.audioTextError}
              isGenerating={generationState.status === 'pending'}
              isGeneratingAudio={isGeneratingAudio}
              profileUrl={tabData.profileUrl}
              projectId={config.project_id}
              sender={config.sender}
              compact={true}
            />
          )}
        </div>

        {/* Right Column - Message Configuration */}
        <div>
          <MessageConfig
            config={config}
            onConfigChange={setConfig}
            projects={projects}
            senders={senders}
            isLoadingProjects={projects.length === 0}
            isLoadingSenders={isLoadingSenders}
            isGenerating={generationState.status === "pending"}
            onGenerate={handleGenerate}
            onGenerateAudio={() => handleGenerateAudio()}
            onReset={handleReset}
            projectLocked={tabData.status === 'ready'}
            researchLocked={Boolean(tabData.scrapedData?.research)}
          />
        </div>
      </div>

      <MessageHistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        profileUrl={tabData.profileUrl}
        projectId={config.project_id}
      />

      {/* Dev Mode Panel */}
      {devModeEnabled && tabData.devModeData && (
        <DevModePanel data={tabData.devModeData} />
      )}
    </div>
  );
}
